import {
  ButtonV2,
  DatePickerV2,
  labraUIDateFormat,
  TextInputV4,
} from '@labrav/react-components'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import {
  MicrosoftCloudSettingsSections,
  FlyoutCloudSettingsSectionsProps,
  TestConnectionStatusType,
} from '../../../../../modules/flyOutOnboarding/types'
import { useStyles } from './ConnectCloudAccount.styles'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload,
  faUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import { debounce } from 'lodash'
import {
  fromIsoDateTimeForDatePicker,
  reportFrontendError,
  toIsoDateTimeString,
  updateAppAlert,
} from '@labrav/common'
import { Persona } from '../../../../FlyOutCloudSettings/Persona'
import { CloudType } from '../../../../../types'
import { ShowConnectionStatuses } from '../../../../FlyOutCloudSettings'
import { checkMicrosoftConnection } from '../../../../../modules/flyOutOnboarding/action'
import { useDispatch } from '../../../../../store'
import FileDownload from 'js-file-download'
import {
  azureDownloadScriptUrl,
  azureManualSetupGuideUrl,
  azureScriptGuideUrl,
} from '../../../../../common/utils/constants'
import {
  ClientSecretExpirationBanner,
  type ClientSecretExpirationStatus,
} from '../../../../ClientSecretExpirationBanner/ClientSecretExpirationBanner'
import {
  clientSecretExpirationDateField,
  CLIENT_SECRET_EXPIRED_ERROR_MESSAGE,
} from './constants'

export interface ConnectCloudAccountRef {
  testConnection: () => Promise<TestConnectionStatusType>
}

export const ConnectCloudAccount = React.forwardRef<
  ConnectCloudAccountRef,
  {
    isFormLocked: boolean
    accountId?: string
  }
>(({ isFormLocked, accountId: accountIdFromProps }, ref) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const accountId = accountIdFromProps
  const formikProps = useFormikContext<FlyoutCloudSettingsSectionsProps>()
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    validateField,
  } = formikProps

  const clientSecretExpirationDatePath = `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.clientSecretExpirationDate`

  const handleClientSecretExpirationDateInteract = useCallback(() => {
    void setFieldTouched(clientSecretExpirationDatePath, true, false)
    void validateField(clientSecretExpirationDatePath)
  }, [setFieldTouched, validateField, clientSecretExpirationDatePath])

  const handleClientSecretExpirationDateChange = useCallback(
    async (date: string | null) => {
      const updatedDate = date ? toIsoDateTimeString(date) : ''
      await setFieldValue(clientSecretExpirationDatePath, updatedDate, false)
      await setFieldTouched(clientSecretExpirationDatePath, true, false)
      await validateField(clientSecretExpirationDatePath)
    },
    [
      setFieldValue,
      setFieldTouched,
      validateField,
      clientSecretExpirationDatePath,
    ]
  )

  const [testConnectionStatus, setTestConnectionStatus] = useState<{
    status: TestConnectionStatusType
    error?: string
  }>({ status: values.cloudCredentials.connectionTested || 'standBy' })

  const debouncedSetFieldValue = useCallback(
    debounce((name, value) => {
      setFieldValue(name, value)
    }, 500),
    []
  )
  const formChangeHandler = useCallback(
    (keyName: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        debouncedSetFieldValue(keyName, event.target.value)
      },
    [debouncedSetFieldValue]
  )
  const hasError = (field: keyof typeof values.cloudCredentials) =>
    touched.cloudCredentials?.[field] &&
    Boolean(errors.cloudCredentials?.[field])

  const getError = (field: keyof typeof values.cloudCredentials) =>
    touched.cloudCredentials?.[field] && errors.cloudCredentials?.[field]

  const [isDownloading, setIsDownloading] = useState(false)
  const [clientSecretExpirationStatus, setClientSecretExpirationStatus] =
    useState<ClientSecretExpirationStatus | null>(null)

  const handleClientSecretExpirationStatusChange = useCallback(
    (status: ClientSecretExpirationStatus | null) => {
      setClientSecretExpirationStatus(status)
    },
    []
  )

  const isClientSecretExpired = clientSecretExpirationStatus?.type === 'expired'

  useEffect(() => {
    if (!isClientSecretExpired) {
      return
    }

    void setFieldTouched(clientSecretExpirationDatePath, true, false)
    void setFieldError(
      clientSecretExpirationDatePath,
      CLIENT_SECRET_EXPIRED_ERROR_MESSAGE
    )
    void setFieldValue(
      `${MicrosoftCloudSettingsSections.STEPS}.cloudCredentials`,
      false
    )
  }, [
    isClientSecretExpired,
    clientSecretExpirationDatePath,
    setFieldTouched,
    setFieldError,
    setFieldValue,
  ])

  const areAllCredentialsFilled = useMemo(() => {
    const hasNoErrors =
      !errors.cloudCredentials?.clientId &&
      !errors.cloudCredentials?.clientSecret &&
      !errors.cloudCredentials?.tenantId &&
      !errors.cloudCredentials?.mpnId
    return (
      values.cloudCredentials.clientId &&
      values.cloudCredentials.clientSecret &&
      values.cloudCredentials.tenantId &&
      values.cloudCredentials.mpnId &&
      hasNoErrors
    )
  }, [values.cloudCredentials, errors.cloudCredentials])

  const testConnection =
    useCallback(async (): Promise<TestConnectionStatusType> => {
      if (accountId && areAllCredentialsFilled) {
        setTestConnectionStatus({ status: 'loading' })
        setFieldValue(
          `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.connectionTested`,
          'loading'
        )
        const response = (await dispatch(
          checkMicrosoftConnection(accountId, true, {
            client_id: values.cloudCredentials.clientId,
            client_secret: values.cloudCredentials.clientSecret,
            tenant_id: values.cloudCredentials.tenantId,
            mpn_id: values.cloudCredentials.mpnId,
          })
        )) as unknown as Record<string, unknown>
        const status = response.status as TestConnectionStatusType
        setTestConnectionStatus({
          status,
        })
        setFieldValue(
          `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.connectionTested`,
          status
        )
        return status
      }
      return 'standBy'
    }, [
      accountId,
      areAllCredentialsFilled,
      values.cloudCredentials.clientId,
      values.cloudCredentials.clientSecret,
      values.cloudCredentials.tenantId,
      values.cloudCredentials.mpnId,
      dispatch,
      setFieldValue,
    ])

  useImperativeHandle(ref, () => ({
    testConnection,
  }))

  const handleRetryTestConnection = () => {
    testConnection()
  }

  const handleCredentialBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleBlur(e)
      if (areAllCredentialsFilled) {
        testConnection()
      }
    },
    [handleBlur, areAllCredentialsFilled, testConnection]
  )

  const downloadScript = async () => {
    try {
      setIsDownloading(true)
      const response = await fetch(azureDownloadScriptUrl)
      const blob = await response.blob()
      FileDownload(blob, 'generate_app_creds.sh')
      dispatch(
        updateAppAlert({
          message: 'Script downloaded.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error) {
      reportFrontendError('api.error', error, {
        'http.url': azureDownloadScriptUrl,
        'http.method': 'GET',
        'http.status': 'network_error',
      })
      dispatch(
        updateAppAlert({
          message: 'Failed to download script. Please try again.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      setIsDownloading(false)
    }
  }
  const openScriptGuide = () => {
    window.open(azureScriptGuideUrl, '_blank')
  }
  const openManualSetupGuide = () => {
    window.open(azureManualSetupGuideUrl, '_blank')
  }

  return (
    <div
      className={clsx(classes.connectCloudAccountContainer)}
      data-testid="microsoft-connect-cloud-account"
    >
      <ClientSecretExpirationBanner
        variant="note"
        expirationDate={values.cloudCredentials.clientSecretExpirationDate}
        fetchOnboardingIfMissing={false}
        showNotConfiguredMessage={true}
        hideSettingsPath
        onStatusChange={handleClientSecretExpirationStatusChange}
      />
      <Typography className={classes.connectCloudAccountHeading}>
        Connect your Microsoft cloud account using either the automated script
        or the manual setup guide, then paste the generated values here to
        complete your configuration.
      </Typography>
      <div className={classes.fieldRow}>
        <ButtonV2
          styleType="outlined"
          endIcon={
            <FontAwesomeIcon icon={faDownload} className={classes.endIcon} />
          }
          onClick={downloadScript}
          smallVariant={true}
          disabled={isDownloading}
          loading={isDownloading}
        >
          Download script
        </ButtonV2>
        <ButtonV2
          styleType="link"
          endIcon={
            <FontAwesomeIcon
              icon={faUpRightFromSquare}
              className={classes.endIcon}
            />
          }
          onClick={openScriptGuide}
          smallVariant={true}
          disabled={false}
        >
          Open Script guide
        </ButtonV2>
        <div className={classes.verticalDeviderButtons} />
        <ButtonV2
          styleType="link"
          endIcon={
            <FontAwesomeIcon
              icon={faUpRightFromSquare}
              className={classes.endIcon}
            />
          }
          onClick={openManualSetupGuide}
          smallVariant={true}
          disabled={false}
        >
          Open manual setup guide
        </ButtonV2>
      </div>
      <div className={classes.fieldRow}>
        <div className={classes.field}>
          <TextInputV4
            keyName="connectionName"
            title="Connection name"
            type="text"
            placeHolder=""
            value={values.cloudCredentials.connectionName as string}
            isReadOnly={isFormLocked as boolean}
            onChange={formChangeHandler(
              `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.connectionName`
            )}
            isRequired={true}
            isError={hasError('connectionName') || false}
            errorText={getError('connectionName')}
            showSpinner={false}
            onBlur={handleBlur}
          />
        </div>

        <div className={classes.field}>
          <TextInputV4
            keyName="tenantId"
            title="Tenant ID"
            type="text"
            placeHolder=""
            value={values.cloudCredentials.tenantId as string}
            isError={hasError('tenantId') || false}
            isReadOnly={isFormLocked as boolean}
            onChange={formChangeHandler(
              `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.tenantId`
            )}
            isRequired={true}
            errorText={getError('tenantId')}
            showSpinner={false}
            onBlur={handleCredentialBlur}
          />
        </div>
        <div className={classes.field}>
          <TextInputV4
            keyName="mpnId"
            title="MPN ID"
            type="text"
            placeHolder=""
            value={values.cloudCredentials.mpnId as string}
            isError={hasError('mpnId') || false}
            isReadOnly={isFormLocked as boolean}
            onChange={formChangeHandler(
              `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.mpnId`
            )}
            isRequired={true}
            errorText={getError('mpnId')}
            showSpinner={false}
            onBlur={handleCredentialBlur}
          />
        </div>
      </div>
      <div className={classes.secondaryFieldRow}>
        <div className={classes.field}>
          <TextInputV4
            keyName="clientId"
            title="Client ID (Application Id)"
            type="text"
            placeHolder=""
            value={values.cloudCredentials.clientId as string}
            isError={hasError('clientId') || false}
            isReadOnly={isFormLocked as boolean}
            onChange={formChangeHandler(
              `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.clientId`
            )}
            isRequired={true}
            errorText={getError('clientId')}
            showSpinner={false}
            onBlur={handleCredentialBlur}
          />
        </div>

        <div className={classes.field}>
          <TextInputV4
            keyName="clientSecret"
            title="Client secret"
            type="text"
            placeHolder=""
            value={values.cloudCredentials.clientSecret as string}
            isError={hasError('clientSecret') || false}
            isReadOnly={isFormLocked as boolean}
            onChange={formChangeHandler(
              `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.clientSecret`
            )}
            isRequired={true}
            errorText={getError('clientSecret')}
            showSpinner={false}
            onBlur={handleCredentialBlur}
          />
        </div>
        <div
          className={classes.field}
          onMouseDown={handleClientSecretExpirationDateInteract}
        >
          <DatePickerV2
            format={labraUIDateFormat}
            size="small"
            field={{
              ...clientSecretExpirationDateField,
              defaultValue: fromIsoDateTimeForDatePicker(
                values.cloudCredentials.clientSecretExpirationDate
              ),
              isReadOnly: isFormLocked,
            }}
            views={['year', 'month', 'day']}
            key="clientSecretExpirationDate"
            fieldId={clientSecretExpirationDatePath}
            handleDateChange={handleClientSecretExpirationDateChange}
            showErrorHelper={
              hasError('clientSecretExpirationDate') || isClientSecretExpired
            }
            errorHelperText={
              isClientSecretExpired
                ? CLIENT_SECRET_EXPIRED_ERROR_MESSAGE
                : String(getError('clientSecretExpirationDate') || '')
            }
          />
        </div>
      </div>
      {testConnectionStatus.status !== 'standBy' && (
        <div className={classes.testConnection} data-testid="test-connection">
          <ShowConnectionStatuses
            testConnectionStatus={testConnectionStatus}
            onRetry={handleRetryTestConnection}
          />
        </div>
      )}
    </div>
  )
})

ConnectCloudAccount.displayName = 'ConnectCloudAccount'

export const ConnectCloudAccountActions: React.FC = () => {
  const { classes } = useStyles()
  return (
    <>
      <div
        className={classes.personaSection}
        data-testid="connect-cloud-account-persona-section"
      >
        <Persona memberType="admin" cloudType={CloudType.AZURE} />
        <div className={classes.verticalDevider} />
      </div>
    </>
  )
}
